import { DateTime } from 'luxon';
import { RouteLocationRaw } from 'vue-router';

import {
  CurrentUser,
  DomainBranding,
  DomainReference,
} from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

import { EmbeddableLocation } from './EmbeddableLocation';

export type DomainBrandingRefreshPayload = {
  result: RecentlyViewedResult;
  domainBranding: DomainBranding;
};

export class RecentlyViewedResult {
  id?: number;
  type?: 'ORDER' | 'SHIPMENT';
  scac?: string;
  location?: EmbeddableLocation;
  requestedAppointmentEarliest?: DateTime;
  requestedAppointmentLatest?: DateTime;
  scheduledAppointmentEarliest?: DateTime;
  scheduledAppointmentLatest?: DateTime;
  expectedArrival?: DateTime;
  actualArrival?: DateTime;
  dateTimeCreated: DateTime = DateTime.local();
  selectedDomain?: DomainReference;
  selectedDomainBranding?: DomainBranding;
  currentUser?: CurrentUser;
  searchType = 'Recent' as const;

  constructor(opts?: Partial<RecentlyViewedResult>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.type != null) {
      this.type = opts.type;
    }
    if (opts?.scac != null) {
      this.scac = opts.scac;
    }
    if (opts?.location != null) {
      this.location = new EmbeddableLocation(opts.location);
    }
    if (opts?.requestedAppointmentEarliest != null) {
      this.requestedAppointmentEarliest = loadLuxonDateTime(
        opts?.requestedAppointmentEarliest,
        opts?.location?.timeZone,
      );
    }
    if (opts?.requestedAppointmentLatest != null) {
      this.requestedAppointmentLatest = loadLuxonDateTime(
        opts?.requestedAppointmentLatest,
        opts?.location?.timeZone,
      );
    }
    if (opts?.scheduledAppointmentEarliest != null) {
      this.scheduledAppointmentEarliest = loadLuxonDateTime(
        opts?.scheduledAppointmentEarliest,
        opts?.location?.timeZone,
      );
    }
    if (opts?.scheduledAppointmentLatest != null) {
      this.scheduledAppointmentLatest = loadLuxonDateTime(
        opts?.scheduledAppointmentLatest,
        opts?.location?.timeZone,
      );
    }
    if (opts?.expectedArrival != null) {
      this.expectedArrival = loadLuxonDateTime(
        opts?.expectedArrival,
        opts?.location?.timeZone,
      );
    }
    if (opts?.actualArrival != null) {
      this.actualArrival = loadLuxonDateTime(
        opts?.actualArrival,
        opts?.location?.timeZone,
      );
    }
    if (opts?.dateTimeCreated != null) {
      this.dateTimeCreated = DateTime.fromISO(opts.dateTimeCreated.toString());
    }
    if (opts?.selectedDomain != null) {
      this.selectedDomain = new DomainReference(opts.selectedDomain);
    }
    if (opts?.selectedDomainBranding != null) {
      this.selectedDomainBranding = new DomainBranding(
        opts.selectedDomainBranding,
      );
    }
    if (opts?.currentUser != null) {
      this.currentUser = new CurrentUser(opts.currentUser);
    }
  }

  getRelativeTime(): string | null {
    return this.dateTimeCreated.toRelative({ style: 'short' });
  }

  public get route(): RouteLocationRaw {
    const name = this.type === 'ORDER' ? 'orderDetail' : 'shipmentDetail';
    const paramsKey = this.type === 'ORDER' ? 'orderId' : 'shipmentId';
    const params = { [paramsKey]: this.id };

    return { name, params };
  }
}
